import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/layout/PageHeader';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  Monitor,
  Search,
  Play,
  Square,
  Trash2,
  RefreshCw,
  HardDrive,
  FileText,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  AlertTriangle,
  Mail,
  CheckCircle2,
  Users,
  Loader2,
} from 'lucide-react';

const RUNNER_LABELS: Record<string, string> = {
  'claude-code': 'VS Code',
  'gift-ui': 'GIFT UI',
  acpx: 'GIFT Agent',
  'acpx-experimental': 'GIFT Agent Experimental',
  'acpx-team': 'GIFT Agent Team',
};

interface AdminSession {
  id: string;
  userId: string;
  projectId: string;
  containerId: string | null;
  status: 'pending' | 'running' | 'stopped';
  containerStatus: string;
  cpuCores: number;
  memoryGb: number;
  codeServerPort: number | null;
  previewPort: number | null;
  previewPort2: number | null;
  runnerType?: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    name: string | null;
    avatar: string | null;
  } | null;
  project: {
    id: string;
    name: string;
  } | null;
}

interface SessionStats {
  totalUsers: number;
  pendingApproval: number;
  activeUsers: number;
  activeSessions: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function AdminSessionsPage() {
  const { t, i18n } = useTranslation(['adminSessions', 'common']);

  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [stats, setStats] = useState<SessionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [refreshing, setRefreshing] = useState(false);

  // Modal states
  const [selectedSession, setSelectedSession] = useState<AdminSession | null>(
    null,
  );
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [logs, setLogs] = useState<string>('');
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Confirm dialog state
  const [confirmAction, setConfirmAction] = useState<{
    type: 'stop' | 'restart';
    sessionId: string;
    sessionLabel: string;
  } | null>(null);

  // Maintenance modal states
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceScheduledAt, setMaintenanceScheduledAt] = useState('');
  const [maintenanceSending, setMaintenanceSending] = useState(false);
  const [maintenanceResult, setMaintenanceResult] = useState<{
    sessionsStopped: number;
    sessionsFailedToStop: number;
    emailsSent: number;
    emailsFailed: number;
    totalUsersNotified: number;
  } | null>(null);

  const fetchSessions = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });

      if (statusFilter && statusFilter !== 'all') {
        params.set('status', statusFilter);
      }

      const response = await fetch(`/api/admin/sessions?${params}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setSessions(data.data.sessions);
        setPagination(data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error(t('adminSessions:errors.loadSessionsFailed'));
    }
  }, [pagination.page, pagination.limit, statusFilter, t]);

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/admin/stats', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error(t('adminSessions:errors.loadStatsFailed'));
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSessions(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSessions]);

  // Auto-poll sessions every 10 seconds to detect container exits
  useEffect(() => {
    if (loading) return;
    const interval = setInterval(() => {
      fetchSessions();
      fetchStats();
    }, 10000);
    return () => clearInterval(interval);
  }, [loading, fetchSessions]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchSessions(), fetchStats()]);
    setRefreshing(false);
  };

  const stopSession = async (sessionId: string) => {
    setActionLoading(sessionId);
    try {
      const response = await fetch(`/api/admin/sessions/${sessionId}/stop`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        await fetchSessions();
        await fetchStats();
      } else {
        const data = await response.json();
        toast.error(data.error || t('adminSessions:errors.stopSessionFailed'));
      }
    } catch (error) {
      console.error('Failed to stop session:', error);
      toast.error(t('adminSessions:errors.stopSessionFailed'));
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };

  const restartSession = async (sessionId: string) => {
    setActionLoading(sessionId);
    try {
      const response = await fetch(`/api/admin/sessions/${sessionId}/restart`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        await fetchSessions();
        await fetchStats();
      } else {
        const data = await response.json();
        toast.error(
          data.error || t('adminSessions:errors.restartSessionFailed'),
        );
      }
    } catch (error) {
      console.error('Failed to restart session:', error);
      toast.error(t('adminSessions:errors.restartSessionFailed'));
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };

  const deleteSession = async () => {
    if (!selectedSession) return;

    setActionLoading(selectedSession.id);
    try {
      const response = await fetch(
        `/api/admin/sessions/${selectedSession.id}`,
        {
          method: 'DELETE',
          credentials: 'include',
        },
      );

      if (response.ok) {
        setShowDeleteModal(false);
        setSelectedSession(null);
        await fetchSessions();
        await fetchStats();
      } else {
        const data = await response.json();
        toast.error(
          data.error || t('adminSessions:errors.deleteSessionFailed'),
        );
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
      toast.error(t('adminSessions:errors.deleteSessionFailed'));
    } finally {
      setActionLoading(null);
    }
  };

  const fetchLogs = async (sessionId: string) => {
    setLoadingLogs(true);
    try {
      const response = await fetch(
        `/api/admin/sessions/${sessionId}/logs?tail=200`,
        {
          credentials: 'include',
        },
      );

      if (response.ok) {
        const data = await response.json();
        setLogs(data.data.logs);
      } else {
        setLogs(t('adminSessions:logs.loadFailed'));
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      setLogs(t('adminSessions:logs.loadFailed'));
    } finally {
      setLoadingLogs(false);
    }
  };

  const openLogsModal = (session: AdminSession) => {
    setSelectedSession(session);
    setShowLogsModal(true);
    fetchLogs(session.id);
  };

  const openMaintenanceModal = () => {
    setMaintenanceMessage('');
    setMaintenanceScheduledAt('');
    setMaintenanceResult(null);
    setShowMaintenanceModal(true);
  };

  const sendMaintenanceNotification = async () => {
    if (!maintenanceMessage.trim()) return;

    setMaintenanceSending(true);
    try {
      const body: { message: string; scheduledAt?: string } = {
        message: maintenanceMessage.trim(),
      };
      if (maintenanceScheduledAt) {
        body.scheduledAt = maintenanceScheduledAt;
      }

      const response = await fetch('/api/admin/maintenance/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        setMaintenanceResult(data.data);
        await Promise.all([fetchSessions(), fetchStats()]);
      } else {
        const data = await response.json();
        toast.error(data.error || t('adminSessions:errors.maintenanceFailed'));
      }
    } catch (error) {
      console.error('Failed to send maintenance notification:', error);
      toast.error(t('adminSessions:errors.maintenanceFailed'));
    } finally {
      setMaintenanceSending(false);
    }
  };

  const getStatusBadge = (status: string, containerStatus: string) => {
    if (status === 'running' && containerStatus === 'running') {
      return (
        <Badge variant="running">{t('adminSessions:status.running')}</Badge>
      );
    }
    if (status === 'running' && containerStatus !== 'running') {
      return (
        <Badge variant="warning">
          {t('adminSessions:status.unhealthy', { status: containerStatus })}
        </Badge>
      );
    }
    if (status === 'stopped') {
      return (
        <Badge variant="stopped">{t('adminSessions:status.stopped')}</Badge>
      );
    }
    if (status === 'pending') {
      return (
        <Badge variant="pending">{t('adminSessions:status.pending')}</Badge>
      );
    }
    return <Badge variant="secondary">{status}</Badge>;
  };

  const getContainerStatusIndicator = (containerStatus: string) => {
    switch (containerStatus) {
      case 'running':
        return <StatusIndicator status="running" size="sm" />;
      case 'exited':
      case 'stopped':
        return <StatusIndicator status="stopped" size="sm" />;
      case 'not_found':
        return <StatusIndicator status="error" size="sm" />;
      default:
        return <StatusIndicator status="pending" size="sm" />;
    }
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat(i18n.language, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('adminSessions:title')}
          description={t('adminSessions:description')}
        />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-14 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const handleConfirmAction = () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'stop') stopSession(confirmAction.sessionId);
    else if (confirmAction.type === 'restart')
      restartSession(confirmAction.sessionId);
  };

  const confirmDialogConfig = confirmAction
    ? {
        stop: {
          title: t('adminSessions:confirm.stopTitle'),
          description: t('adminSessions:confirm.stopDescription', {
            session: confirmAction.sessionLabel,
          }),
          confirmLabel: t('adminSessions:actions.stop'),
          variant: 'default' as const,
        },
        restart: {
          title: t('adminSessions:confirm.restartTitle'),
          description: t('adminSessions:confirm.restartDescription', {
            session: confirmAction.sessionLabel,
          }),
          confirmLabel: t('adminSessions:actions.restart'),
          variant: 'default' as const,
        },
      }[confirmAction.type]
    : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('adminSessions:title')}
        description={t('adminSessions:description')}
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={openMaintenanceModal}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              {t('adminSessions:actions.maintenance')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`}
              />
              {t('adminSessions:actions.refresh')}
            </Button>
          </div>
        }
      />

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Monitor className="h-5 w-5 flex-shrink-0 text-primary" />
              <span className="text-sm text-muted-foreground truncate">
                {t('adminSessions:stats.activeSessions')}
              </span>
            </div>
            <p className="text-2xl font-bold text-primary mt-2">
              {stats.activeSessions}
            </p>
          </div>
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Square className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
              <span className="text-sm text-muted-foreground truncate">
                {t('adminSessions:stats.totalSessions')}
              </span>
            </div>
            <p className="text-2xl font-bold mt-2">{pagination.total}</p>
          </div>
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 flex-shrink-0 text-success" />
              <span className="text-sm text-muted-foreground truncate">
                {t('adminSessions:stats.activeUsers')}
              </span>
            </div>
            <p className="text-2xl font-bold text-success mt-2">
              {stats.activeUsers}
            </p>
          </div>
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <HardDrive className="h-5 w-5 flex-shrink-0 text-warning" />
              <span className="text-sm text-muted-foreground truncate">
                {t('adminSessions:stats.resourcesInUse')}
              </span>
            </div>
            <div className="mt-2 space-y-0.5">
              <p className="text-sm whitespace-nowrap">
                <span className="font-bold text-warning">
                  {sessions
                    .filter((s) => s.status === 'running')
                    .reduce((acc, s) => acc + s.cpuCores, 0)}
                </span>
                <span className="text-muted-foreground">
                  {' '}
                  {t('adminSessions:stats.cpu')}
                </span>
              </p>
              <p className="text-sm whitespace-nowrap">
                <span className="font-bold text-warning">
                  {sessions
                    .filter((s) => s.status === 'running')
                    .reduce((acc, s) => acc + s.memoryGb, 0)}
                </span>
                <span className="text-muted-foreground">
                  {' '}
                  {t('adminSessions:stats.ram')}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('adminSessions:filters.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            aria-label={t('adminSessions:filters.searchAriaLabel')}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue
              placeholder={t('adminSessions:filters.statusPlaceholder')}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t('adminSessions:filters.allStatuses')}
            </SelectItem>
            <SelectItem value="running">
              {t('adminSessions:status.running')}
            </SelectItem>
            <SelectItem value="stopped">
              {t('adminSessions:status.stopped')}
            </SelectItem>
            <SelectItem value="pending">
              {t('adminSessions:status.pending')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Sessions Table or Empty State */}
      {sessions.length === 0 ? (
        <EmptyState
          icon={Monitor}
          title={t('adminSessions:empty.title')}
          description={t('adminSessions:empty.description')}
        />
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminSessions:table.user')}</TableHead>
                  <TableHead>{t('adminSessions:table.project')}</TableHead>
                  <TableHead>{t('adminSessions:table.runnerType')}</TableHead>
                  <TableHead>{t('adminSessions:table.status')}</TableHead>
                  <TableHead>{t('adminSessions:table.resources')}</TableHead>
                  <TableHead>{t('adminSessions:table.created')}</TableHead>
                  <TableHead className="text-right">
                    {t('adminSessions:table.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions
                  .filter((session) => {
                    if (!search) return true;
                    const searchLower = search.toLowerCase();
                    return (
                      session.user?.email
                        ?.toLowerCase()
                        .includes(searchLower) ||
                      session.user?.name?.toLowerCase().includes(searchLower) ||
                      session.project?.name
                        ?.toLowerCase()
                        .includes(searchLower) ||
                      session.id.toLowerCase().includes(searchLower)
                    );
                  })
                  .map((session) => (
                    <TableRow key={session.id}>
                      <TableCell>
                        {session.user ? (
                          <div className="flex items-center gap-2">
                            {session.user.avatar && (
                              <img
                                src={session.user.avatar}
                                alt=""
                                className="h-6 w-6 rounded-full"
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium">
                                {session.user.name || t('common:noName')}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {session.user.email}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">
                            {t('adminSessions:table.unknown')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {session.project ? (
                          <Link
                            to={`/projects/${session.project.id}`}
                            className="text-sm font-medium hover:underline text-primary"
                          >
                            {session.project.name}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">
                            {t('adminSessions:table.unknown')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {t(
                            `adminSessions:runners.${session.runnerType || ''}`,
                            {
                              defaultValue:
                                RUNNER_LABELS[session.runnerType || ''] ||
                                session.runnerType ||
                                t('adminSessions:table.unknown'),
                            },
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {getContainerStatusIndicator(
                              session.containerStatus,
                            )}
                            {getStatusBadge(
                              session.status,
                              session.containerStatus,
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">
                          {t('adminSessions:table.resourceSummary', {
                            cpu: session.cpuCores,
                            memory: session.memoryGb,
                          })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {formatDate(session.createdAt)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {actionLoading === session.id ? (
                          <Loader2 className="h-4 w-4 ml-auto animate-spin" />
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                aria-label={t(
                                  'adminSessions:table.moreActions',
                                )}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {session.status === 'running' &&
                                session.containerStatus === 'running' && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setConfirmAction({
                                        type: 'stop',
                                        sessionId: session.id,
                                        sessionLabel:
                                          session.user?.email ||
                                          session.id.slice(0, 12),
                                      })
                                    }
                                    className="text-warning"
                                  >
                                    <Square className="h-4 w-4 mr-2" />
                                    {t('adminSessions:actions.stop')}
                                  </DropdownMenuItem>
                                )}
                              {session.containerId &&
                                session.containerStatus !== 'not_found' &&
                                session.status !== 'running' && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setConfirmAction({
                                        type: 'restart',
                                        sessionId: session.id,
                                        sessionLabel:
                                          session.user?.email ||
                                          session.id.slice(0, 12),
                                      })
                                    }
                                    className="text-success"
                                  >
                                    <Play className="h-4 w-4 mr-2" />
                                    {t('adminSessions:actions.restart')}
                                  </DropdownMenuItem>
                                )}
                              {session.containerId && (
                                <DropdownMenuItem
                                  onClick={() => openLogsModal(session)}
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  {t('adminSessions:actions.viewLogs')}
                                </DropdownMenuItem>
                              )}
                              {session.status === 'running' && (
                                <DropdownMenuItem asChild>
                                  <Link
                                    to={`/sessions/${session.id}`}
                                    target="_blank"
                                  >
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    {t('adminSessions:actions.openSession')}
                                  </Link>
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedSession(session);
                                  setShowDeleteModal(true);
                                }}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                {t('adminSessions:actions.delete')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-muted-foreground">
                {t('adminSessions:pagination.showing', {
                  start: (pagination.page - 1) * pagination.limit + 1,
                  end: Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  ),
                  total: pagination.total,
                })}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setPagination({ ...pagination, page: pagination.page - 1 })
                  }
                  disabled={pagination.page === 1}
                  aria-label={t('adminSessions:pagination.previous')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">
                  {t('adminSessions:pagination.pageOf', {
                    page: pagination.page,
                    totalPages: pagination.totalPages,
                  })}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setPagination({ ...pagination, page: pagination.page + 1 })
                  }
                  disabled={pagination.page === pagination.totalPages}
                  aria-label={t('adminSessions:pagination.next')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stop/Restart Confirm Dialog */}
      <ConfirmDialog
        open={!!confirmAction}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        title={confirmDialogConfig?.title || ''}
        description={confirmDialogConfig?.description || ''}
        confirmLabel={confirmDialogConfig?.confirmLabel}
        cancelLabel={t('common:cancel')}
        variant={confirmDialogConfig?.variant}
        onConfirm={handleConfirmAction}
        loading={!!actionLoading}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={showDeleteModal}
        onOpenChange={(open) => {
          setShowDeleteModal(open);
          if (!open) setSelectedSession(null);
        }}
        title={t('adminSessions:deleteDialog.title')}
        description={t('adminSessions:deleteDialog.description')}
        confirmLabel={t('adminSessions:deleteDialog.confirm')}
        cancelLabel={t('common:cancel')}
        variant="destructive"
        onConfirm={deleteSession}
        loading={actionLoading === selectedSession?.id}
      >
        {selectedSession && (
          <div className="bg-muted rounded-lg p-4 my-2">
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium">
                  {t('adminSessions:deleteDialog.sessionId')}:
                </span>{' '}
                <span className="font-mono">{selectedSession.id}</span>
              </div>
              <div>
                <span className="font-medium">
                  {t('adminSessions:table.user')}:
                </span>{' '}
                {selectedSession.user?.email ||
                  t('adminSessions:table.unknown')}
              </div>
              <div>
                <span className="font-medium">
                  {t('adminSessions:table.project')}:
                </span>{' '}
                {selectedSession.project?.name ||
                  t('adminSessions:table.unknown')}
              </div>
              <div>
                <span className="font-medium">
                  {t('adminSessions:table.status')}:
                </span>{' '}
                {t(`adminSessions:status.${selectedSession.status}`, {
                  defaultValue: selectedSession.status,
                })}
              </div>
            </div>
          </div>
        )}
      </ConfirmDialog>

      {/* Logs Modal */}
      <Dialog open={showLogsModal} onOpenChange={setShowLogsModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{t('adminSessions:logs.title')}</DialogTitle>
            <DialogDescription>
              {t('adminSessions:logs.description', {
                sessionId: selectedSession?.id.slice(0, 12) || '-',
                containerId: selectedSession?.containerId?.slice(0, 12) || '-',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 py-4">
            {loadingLogs ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-4 rounded" />
                ))}
              </div>
            ) : (
              <pre className="bg-muted p-4 rounded-lg overflow-auto h-full max-h-[50vh] text-xs font-mono whitespace-pre-wrap break-all">
                {logs || t('adminSessions:logs.empty')}
              </pre>
            )}
          </div>
          <DialogFooter className="flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => selectedSession && fetchLogs(selectedSession.id)}
              disabled={loadingLogs}
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${loadingLogs ? 'animate-spin' : ''}`}
              />
              {t('adminSessions:logs.refresh')}
            </Button>
            <Button onClick={() => setShowLogsModal(false)}>
              {t('common:close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance Modal */}
      <Dialog
        open={showMaintenanceModal}
        onOpenChange={(open) => {
          if (!maintenanceSending) {
            setShowMaintenanceModal(open);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {t('adminSessions:maintenance.title')}
            </DialogTitle>
            <DialogDescription>
              {t('adminSessions:maintenance.description')}
            </DialogDescription>
          </DialogHeader>

          {maintenanceResult ? (
            // Results view
            <div className="space-y-4 py-4">
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>
                  {t('adminSessions:maintenance.result.title')}
                </AlertTitle>
                <AlertDescription>
                  {t('adminSessions:maintenance.result.description')}
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-2 gap-3">
                <Card>
                  <CardContent className="pt-4 pb-3">
                    <div className="text-sm text-muted-foreground">
                      {t('adminSessions:maintenance.result.sessionsStopped')}
                    </div>
                    <div className="text-2xl font-bold">
                      {maintenanceResult.sessionsStopped}
                    </div>
                    {maintenanceResult.sessionsFailedToStop > 0 && (
                      <div className="text-xs text-destructive mt-1">
                        {t('adminSessions:maintenance.result.failedCount', {
                          count: maintenanceResult.sessionsFailedToStop,
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3">
                    <div className="text-sm text-muted-foreground">
                      {t('adminSessions:maintenance.result.emailsSent')}
                    </div>
                    <div className="text-2xl font-bold">
                      {maintenanceResult.emailsSent}
                    </div>
                    {maintenanceResult.emailsFailed > 0 && (
                      <div className="text-xs text-destructive mt-1">
                        {t('adminSessions:maintenance.result.failedCount', {
                          count: maintenanceResult.emailsFailed,
                        })}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      {t('adminSessions:maintenance.result.ofUsers', {
                        count: maintenanceResult.totalUsersNotified,
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <DialogFooter>
                <Button onClick={() => setShowMaintenanceModal(false)}>
                  {t('adminSessions:maintenance.result.done')}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            // Form view
            <div className="space-y-4 py-4">
              {stats && stats.activeSessions > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>
                    {t('adminSessions:maintenance.warningTitle')}
                  </AlertTitle>
                  <AlertDescription>
                    {t('adminSessions:maintenance.warningDescription', {
                      count: stats.activeSessions,
                    })}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="maintenance-message">
                  {t('adminSessions:maintenance.messageLabel')}{' '}
                  <span className="text-destructive">*</span>
                </Label>
                <textarea
                  id="maintenance-message"
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={t(
                    'adminSessions:maintenance.messagePlaceholder',
                  )}
                  value={maintenanceMessage}
                  onChange={(e) => setMaintenanceMessage(e.target.value)}
                  maxLength={5000}
                  disabled={maintenanceSending}
                />
                <div className="text-xs text-muted-foreground text-right">
                  {maintenanceMessage.length}/5000
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="maintenance-scheduled">
                  {t('adminSessions:maintenance.scheduledLabel')}
                </Label>
                <Input
                  id="maintenance-scheduled"
                  type="datetime-local"
                  value={maintenanceScheduledAt}
                  onChange={(e) => setMaintenanceScheduledAt(e.target.value)}
                  disabled={maintenanceSending}
                />
                <p className="text-xs text-muted-foreground">
                  {t('adminSessions:maintenance.scheduledHelp')}
                </p>
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  variant="outline"
                  onClick={() => setShowMaintenanceModal(false)}
                  disabled={maintenanceSending}
                >
                  {t('common:cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={sendMaintenanceNotification}
                  disabled={maintenanceSending || !maintenanceMessage.trim()}
                >
                  {maintenanceSending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('adminSessions:maintenance.sending')}
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      {t('adminSessions:maintenance.sendAction')}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
